import React from "react";
import type { ConnectionPayload } from "../components/MessageInput/Lexical/ConnectionPlugin/ConnectionNode";

export const CONNECTION_REGEX =
  /\{connection:([^}]*):([^}]*):([^}]*):([^}]*)\}/;
export const SKILL_REGEX = /\{skill:([^}]*):([^}]*)\}/;
export const PLACEHOLDER_REGEX = /\{placeholder:([^}]*):([^}]*)\}/;

export interface SkillTagProps {
  skillName: string;
}

export interface PlaceholderTagProps {
  placeholder: string;
}

export interface ConnectionTagProps {
  connection: ConnectionPayload;
}

/**
 * Parses text content and replaces entity references with React components
 * @param content - The text content to parse
 * @param components - Object containing the component renderers for each entity type
 * @returns Array of React nodes containing text and components
 */
export const matchReferencedEntities = (
  content: string,
  components: {
    SkillTag: React.ComponentType<SkillTagProps>;
    PlaceholderTag: React.ComponentType<PlaceholderTagProps>;
    ConnectionTag: React.ComponentType<ConnectionTagProps>;
  },
) => {
  const { SkillTag, PlaceholderTag, ConnectionTag } = components;

  const parts = content.split(
    /({skill:[^}]+}|{connection:[^}]+}|{placeholder:[^}]+})/g,
  );

  return parts.map((part, index) => {
    const skillMatch = part.match(SKILL_REGEX);
    const connectionMatch = part.match(CONNECTION_REGEX);
    const placeholderMatch = part.match(PLACEHOLDER_REGEX);

    if (placeholderMatch) {
      const [, , value] = placeholderMatch;
      return React.createElement(PlaceholderTag, {
        key: index,
        placeholder: value,
      });
    }

    if (skillMatch) {
      const [, idOrName, name] = skillMatch;

      if (!idOrName) {
        return part;
      }

      // handle legacy skill names where we were not using ID
      const skillName = name || idOrName;

      return React.createElement(SkillTag, { key: index, skillName });
    }

    if (connectionMatch) {
      const [, platform, type, id, title] = connectionMatch;

      if (!platform || !type || !id || !title) {
        return part;
      }

      return React.createElement(ConnectionTag, {
        key: index,
        connection: {
          platform,
          type,
          identifier: id,
          title,
        },
      });
    }

    return part;
  });
};
